import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import type {
  ProviderCheckoutDomainSettings,
  UpdateProviderCheckoutDomainInput,
} from '@/types/provider-checkout-domain';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const readBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return undefined;
};

function normalizeCheckoutDomain(value: unknown): string | null {
  const raw = readString(value)?.trim() ?? '';
  if (raw.length === 0) {
    return null;
  }
  return raw;
}

function normalizeCheckoutDomainEnabled(value: unknown): boolean {
  return readBoolean(value) ?? false;
}

function normalizeSettings(payload: ApiPayload | undefined): ProviderCheckoutDomainSettings {
  const record =
    (payload?.data as Record<string, unknown> | undefined) ??
    ((payload as Record<string, unknown> | undefined) ?? {});
  return {
    checkoutDomain: normalizeCheckoutDomain(
      record?.checkout_domain ?? record?.checkoutDomain,
    ),
    checkoutDomainEnabled: normalizeCheckoutDomainEnabled(
      record?.checkout_domain_enabled ?? record?.checkoutDomainEnabled,
    ),
  };
}

export class ProviderCheckoutDomainRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestProviderCheckoutDomain(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderCheckoutDomainRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderCheckoutDomainRequestError('Provider checkout domain request failed', undefined, error);
  }
}

class ProviderCheckoutDomainService {
  async getSettings(): Promise<ProviderCheckoutDomainSettings> {
    const response = await requestProviderCheckoutDomain('/api/v1/provider/settings/checkout-domain');
    return normalizeSettings(response);
  }

  async updateSettings(
    input: UpdateProviderCheckoutDomainInput,
  ): Promise<ProviderCheckoutDomainSettings> {
    const response = await requestProviderCheckoutDomain('/api/v1/provider/settings/checkout-domain', {
      method: 'PUT',
      body: {
        checkout_domain: input.checkoutDomain,
        checkout_domain_enabled: input.checkoutDomainEnabled,
      },
    });
    return normalizeSettings(response);
  }
}

export const providerCheckoutDomainService = new ProviderCheckoutDomainService();

export { normalizeCheckoutDomain as normalizeProviderCheckoutDomain };
